/**
 * Pull requests and alerts.
 *
 * The stuck-PR list is the Action Center's "what do I do right now" tab, and it
 * is the most concrete thing on the demo dashboard - a prospect reads it as
 * "this is what the product would tell me on Monday morning". So the entries
 * are consistent with the rest of the fixture rather than invented: the oldest
 * stuck PRs sit on the repos whose profiles say review is slowest, and their
 * authors are the people those repos actually belong to.
 */

import type { PullRequest, PullRequestListResponse } from '@/types/pr';
import type { Alert, AlertListResponse } from '@/types/alerts';
import {
  DEMO_ORG_ID,
  DEMO_REPOS,
  findDemoDeveloper,
  findDemoRepo,
  findDemoTeam,
  type DemoRepo,
} from '../fixture';
import { demoDate, demoTimestamp, jitter, rngFor, type DemoSeedInput } from '../seed';

export interface DemoPrParams {
  days: number;
  repoId?: string | null;
  teamId?: string | null;
  excludeBots: boolean;
  /** Only return PRs waiting at least this long, matching the real filter. */
  minWaitHours?: number;
}

const seedInput = (params: DemoPrParams, route: string): DemoSeedInput => ({
  route,
  days: params.days,
  repoId: params.repoId,
  teamId: params.teamId,
  excludeBots: params.excludeBots,
});

/** Titles that read like real work rather than lorem ipsum, keyed by repo. */
const TITLES: Record<string, string[]> = {
  'web-app': [
    'Split checkout into a multi-step flow',
    'Replace the legacy session hook',
    'Virtualise the orders table',
  ],
  'payments-service': [
    'Reconcile ledger postings on partial refunds',
    'Retry Stripe webhooks with backoff',
    'Extract the refund state machine',
  ],
  'orders-api': [
    'Add idempotency keys to order creation',
    'Batch fulfilment dispatch',
    'Backfill order status history',
  ],
  'platform-infra': ['Pin the cluster autoscaler version', 'Rotate RDS credentials'],
  'shared-libs': ['Drop the deprecated date helpers', 'Publish typed error codes'],
};

function reposInScope(params: DemoPrParams): DemoRepo[] {
  const scoped = findDemoRepo(params.repoId);
  if (scoped) return [scoped];
  const team = findDemoTeam(params.teamId);
  if (team) return DEMO_REPOS.filter((r) => team.repoIds.includes(r.repository.id));
  return DEMO_REPOS;
}

export function buildDemoPullRequests(params: DemoPrParams): PullRequestListResponse {
  const repos = reposInScope(params);
  const rng = rngFor(seedInput(params, 'pr/list'), 'stuck');
  const minWaitHours = params.minWaitHours ?? 0;
  const prs: PullRequest[] = [];

  let number = 148;
  for (const { repository, profile } of repos) {
    const titles = TITLES[repository.name] ?? TITLES['web-app'];
    titles.forEach((title, index) => {
      // Age scales with how slow the repo actually is, so the worst repo owns
      // the worst PRs - the same story the metrics and risk signals tell.
      const waitHours = Math.round(profile.cycleTimeHours * (1.4 + index * 0.55) * jitter(rng, 0.15));
      if (waitHours < minWaitHours) return;

      const authorId = profile.contributorIds[index % profile.contributorIds.length];
      const author = findDemoDeveloper(authorId);
      const additions = Math.round(profile.avgPrSize * 0.72 * jitter(rng, 0.3));
      const deletions = Math.round(profile.avgPrSize * 0.28 * jitter(rng, 0.4));
      const commitsCount = Math.max(1, Math.round((additions + deletions) / 120));

      prs.push({
        id: `demo-pr-${repository.name}-${number}`,
        organization_id: DEMO_ORG_ID,
        repository_id: repository.id,
        author_id: authorId,
        external_id: `demo-${number}`,
        number,
        title,
        state: 'open',
        head_branch: `feature/${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 32)}`,
        base_branch: 'main',
        created_at: demoTimestamp(waitHours),
        updated_at: demoTimestamp(Math.round(waitHours * 0.3)),
        first_review_requested_at: demoTimestamp(Math.round(waitHours * 0.92)),
        additions,
        deletions,
        lines_changed: additions + deletions,
        changed_files: Math.max(1, Math.round((additions + deletions) / 45)),
        commits_count: commitsCount,
        comments_count: Math.round(rng() * 4),
        review_comments_count: Math.round(rng() * 6),
        merged_without_approval: false,
        has_failing_checks: profile.testFailureRateBase > 10 && index === 0,
        // PR readiness: how much of the work landed AFTER the PR was opened.
        // Without these the "PR Churn" column renders a dash on every row.
        // Scaled by the repo's churn profile, so the churny repo shows churny
        // PRs rather than every PR looking equally tidy.
        commits_after_pr_opened: Math.max(0, Math.round(commitsCount * 0.3 * profile.churnMultiplier * jitter(rng, 0.4))),
        lines_changed_after_pr_opened: Math.round((additions + deletions) * 0.22 * profile.churnMultiplier * jitter(rng, 0.35)),
        pct_work_after_pr_opened: Number(Math.min(85, 22 * profile.churnMultiplier * jitter(rng, 0.3)).toFixed(1)),
        // Drafts are excluded from every stuck/waiting query in the product,
        // so a draft here would be data the real UI would never show.
        is_draft: false,
        cycle_time_hours: waitHours,
        author: author
          ? { id: author.id, external_login: author.login, name: author.name, is_bot: author.isBot }
          : undefined,
        repository: {
          id: repository.id,
          name: repository.name,
          full_name: repository.full_name,
        },
      } as PullRequest);
      number -= 7;
    });
  }

  prs.sort((a, b) => (b.cycle_time_hours ?? 0) - (a.cycle_time_hours ?? 0));

  return {
    pull_requests: prs,
    total: prs.length,
    page: 1,
    page_size: Math.max(prs.length, 1),
    filters_applied: { status: 'waiting', min_wait_hours: minWaitHours },
  };
}

/**
 * Alerts mirror the risk signals rather than being an independent list.
 *
 * The Action Center shows alerts beside the stuck PRs, so an alert about a repo
 * the metrics call healthy would undercut both.
 */
export function buildDemoAlerts(params: DemoPrParams): AlertListResponse {
  const repos = reposInScope(params);
  const rng = rngFor(seedInput(params, 'pr/alerts'), 'alerts');

  const alerts: Alert[] = repos
    .filter((r) => r.profile.cycleTimeHours > 55 || r.profile.reviewCoverageBase < 80)
    .map(({ repository, profile }, index) => {
      const slow = profile.cycleTimeHours > 55;
      return {
        id: `demo-alert-${repository.id}`,
        organization_id: DEMO_ORG_ID,
        alert_rule_id: `demo-rule-${slow ? 'cycle-time' : 'review-coverage'}`,
        metric_type: slow ? 'velocity' : 'quality',
        metric_name: slow ? 'cycle_time_hours' : 'review_coverage',
        entity_type: 'repository',
        entity_id: repository.id,
        metric_value: Number(
          (slow ? profile.cycleTimeHours : profile.reviewCoverageBase) * jitter(rng, 0.05)
        ),
        threshold_value: slow ? 48 : 80,
        severity: index === 0 ? 'critical' : 'warning',
        triggered_at: demoTimestamp(18 + index * 9),
        is_acknowledged: false,
        acknowledged_at: null,
        acknowledged_by: null,
        period_start: demoDate(params.days),
        period_end: demoDate(0),
        created_at: demoTimestamp(18 + index * 9),
        updated_at: demoTimestamp(18 + index * 9),
      } as Alert;
    });

  return { alerts, total: alerts.length, page: 1, page_size: Math.max(alerts.length, 1) };
}
